import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Copy,
  Printer,
  RotateCcw,
  Ban,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Upload,
  History,
  MoreVertical,
  ImageIcon,
  Check,
  Layers,
  Loader2,
} from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/shared/page-header';
import { DataTable } from '@/components/data-table/data-table';
import { useBarcodeScan } from '@/hooks/use-barcode-scan';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProducts,
  useProductBuildings,
  useToggleProductPo,
  useDeactivateProduct,
  useRestoreProduct,
  useDeleteProduct,
  useUpdateProductPrice,
  useRecordPriceChange,
} from '@/hooks/use-products';
import { useCanSeeCost } from '@/hooks/use-pos-config';
import { useCreateImportJob, useFinishImportJob } from '@/hooks/use-import-jobs';
import { useDepartments } from '@/hooks/use-departments';
import { useCategories } from '@/hooks/use-categories';
import { usePayees } from '@/hooks/use-payees';
import { useLocationStore } from '@/stores/location-store';
import {
  decidePoToggle,
  isInPo,
  poToggleLabel,
  poToggleSuccessMessage,
  PO_LOCATION_REQUIRED,
  NO_VENDORS_TITLE,
  NO_VENDORS_MESSAGE,
} from './product-po';
import { useAuthStore } from '@/stores/auth-store';
import { cn, formatCurrency, formatDateTime } from '@/lib/utils';
import { productsApi } from '@/lib/api-client';
import { usePermissions } from '@/hooks/use-permissions';
import { exportBlankTemplate, normalizeCatalogName, parseProductsFromFile } from '@/lib/product-xlsx';

// Cost visibility now resolved via useCanSeeCost (per-location config, falls
// back to the historic super_admin/owner/admin list when no row exists yet).

/** Why one spreadsheet row was rejected, as returned by /products/bulk-import.
 *  Import matches departments, categories and vendors by name against what
 *  already exists; it never creates them, so an unrecognised name is an error
 *  the user fixes in the file (or by creating the thing first). */
interface ImportFailureReason {
  kind: 'department' | 'category' | 'vendor';
  name: string;
  /** Vendor exists but has been deactivated — a different fix from a typo. */
  inactive?: boolean;
}

interface ImportFailure {
  row: number;
  product: string;
  barcode: string | null;
  reasons: ImportFailureReason[];
}

interface ImportFailureSummary extends ImportFailureReason {
  /** How many products this one missing name accounts for. */
  count: number;
}

/** Where the user goes to create the missing thing. */
const FAILURE_FIX_LOCATION: Record<ImportFailureReason['kind'], string> = {
  department: 'POS Management → Departments',
  category: 'POS Management → Categories',
  vendor: 'Send Payments → Vendors',
};

function describeImportFailure(r: ImportFailureReason): string {
  if (r.kind === 'vendor' && r.inactive) {
    return `Vendor "${r.name}" is inactive`;
  }
  return `${r.kind[0].toUpperCase()}${r.kind.slice(1)} "${r.name}" not found`;
}

/** CSV of every rejected row, so the user can work through a long list outside
 *  the modal and hand it back to whoever produced the spreadsheet. */
function downloadImportFailures(failures: ImportFailure[]) {
  const cell = (v: string | number | null) => {
    const str = v == null ? '' : String(v);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const rows = [
    ['Excel row', 'Product', 'Barcode', 'Reason', 'Fix in'],
    ...failures.flatMap((f) =>
      f.reasons.map((r) => [
        f.row,
        f.product,
        f.barcode,
        describeImportFailure(r),
        r.kind === 'vendor' && r.inactive ? 'Send Payments → Vendors' : FAILURE_FIX_LOCATION[r.kind],
      ]),
    ),
  ];
  const csv = rows.map((r) => r.map(cell).join(',')).join('\r\n');
  // BOM so Excel opens it as UTF-8 and the arrows/quotes survive.
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `import-not-imported-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Small 30×30 product thumbnail for the table. Shows the product's own image
// (products.image_url, returned as `imageUrl`) when present; otherwise — or if
// the image fails to load — a neutral image placeholder so every row lines up.
function ProductThumb({ src, alt }: { src?: string | null; alt?: string }) {
  const [errored, setErrored] = useState(false);
  const showImage = !!src && !errored;
  return (
    <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-muted">
      {showImage ? (
        <img
          src={src as string}
          alt={alt || ''}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        <ImageIcon className="h-4 w-4 text-muted-foreground" aria-label="No image" />
      )}
    </div>
  );
}

function SortHeader({
  label,
  field,
  currentSort,
  currentDir,
  onSort,
}: {
  label: string;
  field: string;
  currentSort: string;
  currentDir: string;
  onSort: (field: string) => void;
}) {
  const isActive = currentSort === field;
  return (
    <button
      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground uppercase"
      onClick={() => onSort(field)}
    >
      {label}
      {isActive ? (
        currentDir === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

function BarcodePreview({ value }: { value: string }) {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!svgRef.current || !value) return;
    import('jsbarcode').then(({ default: JsBarcode }) => {
      try {
        const fmt = value.length === 12 || value.length === 13 ? 'EAN13' : value.length === 8 ? 'EAN8' : 'CODE128';
        JsBarcode(svgRef.current!, value, {
          format: fmt,
          width: 2,
          height: 80,
          fontSize: 13,
          margin: 8,
          displayValue: true,
        });
      } catch {
        JsBarcode(svgRef.current!, value, {
          format: 'CODE128',
          width: 2,
          height: 80,
          fontSize: 13,
          margin: 8,
          displayValue: true,
        });
      }
    });
  }, [value]);
  return <svg ref={svgRef} className="w-full" />;
}

// Height the pinned pagination bar occupies under the table, plus the gap
// above it — the table's max-height is computed to leave exactly this free.
const PAGINATION_BAR_HEIGHT = 86;

// All Products only — the other list pages keep their own [10, 25, 50, 100].
// This table is usually scanned in bulk, so it starts at 100 and drops the
// small page sizes rather than making users re-pick every visit.
const PER_PAGE_OPTIONS = [50, 100, 200];
const DEFAULT_PER_PAGE = 100;

// Common thermal label-roll sizes (width × height in mm). The selected size is
// emitted as `@page { size: <w>mm <h>mm }` so the thermal driver uses the label
// stock instead of defaulting to A4/Letter.
const LABEL_SIZES = [
  { id: '40x30', label: '40 × 30 mm', w: 40, h: 30 },
  { id: '50x25', label: '50 × 25 mm', w: 50, h: 25 },
  { id: '57x32', label: '57 × 32 mm', w: 57, h: 32 },
  { id: '60x40', label: '60 × 40 mm', w: 60, h: 40 },
  { id: '76x51', label: '76 × 51 mm (3 × 2 in)', w: 76, h: 51 },
  { id: '100x50', label: '100 × 50 mm (4 × 2 in)', w: 100, h: 50 },
];

function escapeHtml(s: string) {
  return String(s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

function ProductActionsMenu({
  canExportProducts,
  canImportProducts,
  onExportProducts,
  exportProgress,
  onExportTemplate,
  onImportClick,
  importProgress,
}: {
  canExportProducts: boolean;
  canImportProducts: boolean;
  onExportProducts: () => void;
  exportProgress: { done: number; total: number; startedAt: number } | null;
  onExportTemplate: () => void;
  onImportClick: () => void;
  importProgress: { done: number; total: number; startedAt: number } | null;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const close = () => setOpen(false);
  const openMenu = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 220;
    setCoords({ top: rect.bottom + 4, left: Math.max(8, rect.right - menuWidth) });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open]);
  const itemCls =
    'flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed';
  const sectionCls = 'px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : openMenu())}
        title="More actions"
        aria-label="More actions"
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open &&
        coords &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={close} />
            <div
              style={{ top: coords.top, left: coords.left }}
              className="fixed z-50 w-[220px] rounded-lg border border-border bg-white py-1 shadow-lg"
            >
              {/* The whole Export group rides one flag. The blank template is an
                  export of our column contract, so a business that isn't opted
                  in to export shouldn't be handed it either — and gating the
                  section header with it avoids leaving an "Export" label above
                  an empty group. */}
              {canExportProducts && (
                <>
                  <div className={sectionCls}>Export</div>
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      onExportProducts();
                    }}
                    disabled={exportProgress !== null}
                    className={itemCls}
                  >
                    <Download className="h-3.5 w-3.5 shrink-0" />
                    {exportProgress ? 'Exporting...' : 'Export Products'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      onExportTemplate();
                    }}
                    className={itemCls}
                  >
                    <Download className="h-3.5 w-3.5 shrink-0" />
                    Blank Template
                  </button>
                  <div className="my-1 border-t border-border" />
                </>
              )}
              {canImportProducts && (
                <>
                  <div className={sectionCls}>Import</div>
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      onImportClick();
                    }}
                    disabled={importProgress !== null}
                    className={itemCls}
                  >
                    <Upload className="h-3.5 w-3.5 shrink-0" />
                    {importProgress
                      ? `Importing ${importProgress.done.toLocaleString()} / ${importProgress.total.toLocaleString()}...`
                      : 'Import Products'}
                  </button>
                  <a href="#/pos-management/products/import-history" onClick={close} className={itemCls}>
                    <History className="h-3.5 w-3.5 shrink-0" />
                    Import History
                  </a>
                </>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// Inline Selling Price editor for the product list. The read view is an
// underlined price that swaps to a small number input on click (Enter saves,
// Escape cancels). It used to be a pencil that only appeared on row hover,
// which users reported not noticing at all — the underline reads as tappable
// without needing to be discovered first. Saving delegates to the parent's
// onSave, which reuses the same per-location price save + price-history
// recording as the full product edit page.
function InlinePriceCell({
  product,
  locationId,
  readOnly,
  onSave,
}: {
  product: any;
  locationId: string | null;
  readOnly: boolean;
  onSave: (product: any, newPrice: number) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mirror the read-only column's resolution: with a location selected the
  // row's sellingPrice is already that location's price; without one it falls
  // back to the legacy cashPrice.
  const current = locationId ? product.sellingPrice : (product.sellingPrice ?? product.cashPrice);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const startEdit = () => {
    // Prices are per location, so an edit is ambiguous without one — match the
    // export/import guard on this page rather than silently editing an
    // arbitrary location's price.
    if (!locationId) {
      toast.error('Select a location from the dropdown before editing prices.');
      return;
    }
    setValue(current != null ? String(parseFloat(current)) : '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setValue('');
  };

  const save = async () => {
    const next = parseFloat(value);
    if (!Number.isFinite(next) || next < 0) {
      toast.error('Enter a valid price (0 or more)');
      return;
    }
    const old = current != null ? parseFloat(current) : null;
    if (old === next) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(product, next);
      toast.success('Price updated');
      setEditing(false);
    } catch (err: any) {
      toast.error(err?.error?.message || 'Failed to update price');
    } finally {
      setSaving(false);
    }
  };

  if (readOnly) return <span>{formatCurrency(current)}</span>;

  if (!editing) {
    return (
      <button
        type="button"
        onClick={startEdit}
        title="Edit price"
        aria-label={`Edit selling price, currently ${formatCurrency(current)}`}
        className="rounded underline decoration-dotted underline-offset-4 hover:text-primary hover:decoration-solid focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {formatCurrency(current)}
      </button>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <div className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          $
        </span>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0"
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          className="h-8 w-24 rounded-md border border-input bg-background pl-5 pr-2 text-right text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </div>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        title="Save"
        aria-label="Save price"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-green-50 disabled:opacity-50"
      >
        <Check className="h-4 w-4 text-green-600" />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        title="Cancel"
        aria-label="Cancel"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted disabled:opacity-50"
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
}

// SKU / Aisle / Bin used to each own a table column, which cost a lot of width
// for fields most users only check occasionally. They now live in a popover on
// the product name. Portalled to <body> because the table scrolls horizontally
// (overflow-x-auto), which would otherwise clip it. Opens on hover and on
// keyboard focus so it isn't mouse-only, and closes on scroll/resize since the
// coordinates are captured once at open time.
const PRODUCT_DETAIL_FIELDS: Array<{
  label: string;
  key: string;
  format?: (product: any) => string;
  // Renders the label above the value on its own full-width line instead of as
  // a label/value pair, for values too long to share a 220px row.
  stacked?: boolean;
}> = [
  { label: 'SKU', key: 'sku' },
  // Additional barcodes were only visible on the product form, so a code that
  // scans at the register could not be checked from the list. They sit in this
  // popover rather than a column for the same width reason as SKU/Aisle/Bin,
  // and stack + wrap rather than truncate: half of a barcode is worse than
  // none, and a product can carry several.
  {
    label: 'Additional Barcode',
    key: 'additionalBarcodes',
    stacked: true,
    format: (product) => {
      const codes = Array.isArray(product.additionalBarcodes) ? product.additionalBarcodes.filter(Boolean) : [];
      return codes.length > 0 ? codes.join(', ') : '—';
    },
  },
  { label: 'Building', key: 'building' },
  { label: 'Aisle', key: 'aisle' },
  { label: 'Bin/Bay', key: 'bin' },
  // Variations followed SKU/Aisle/Bin out of the table for the same reason —
  // a whole column spent on a number most rows report as zero. Only the count
  // moved here; opening the variant list is an action, and this card is
  // pointer-events-none (so it can't swallow the pointer on its way back to
  // the row), so that button lives in the row's Actions group instead.
  {
    label: 'Variations',
    key: 'variantCount',
    format: (product) => {
      if (product.parentProductId) return 'Variant';
      const count = product.variantCount || 0;
      return count > 0 ? `${count} variant${count > 1 ? 's' : ''}` : '—';
    },
  },
];

function ProductNameWithDetails({ product }: { product: any }) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);

  const open = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 220;
    const estimatedHeight = 180;
    // Flip above the name when there isn't room below, so rows near the bottom
    // of the viewport don't render the card off-screen.
    const below = rect.bottom + 6;
    const top = below + estimatedHeight > window.innerHeight ? Math.max(8, rect.top - estimatedHeight - 6) : below;
    setCoords({ top, left: Math.min(rect.left, window.innerWidth - width - 8) });
  };
  const close = () => setCoords(null);

  useEffect(() => {
    if (!coords) return;
    const handler = () => setCoords(null);
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [coords]);

  return (
    <div>
      {/* The name opens the product page (it replaced the Actions column's
          Edit button); hovering / focusing still shows the details card. */}
      <a
        ref={triggerRef}
        href={`#/pos-management/products/${product.id}/edit`}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        className="inline-block cursor-pointer font-medium underline decoration-dotted decoration-muted-foreground/40 underline-offset-4 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {product.name}
      </a>
      {product.primaryBarcode && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>{product.primaryBarcode}</span>
          {/* Marks a barcode issued by the generator rather than one that
              came off a manufacturer's packaging. */}
          {product.barcodeSource === 'auto' && (
            <span className="rounded bg-primary/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-primary">
              Auto
            </span>
          )}
        </div>
      )}
      {coords &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
            className="pointer-events-none fixed z-50 w-[220px] rounded-lg border border-border bg-white p-3 shadow-lg"
          >
            <dl className="space-y-1.5">
              {PRODUCT_DETAIL_FIELDS.map((f) => {
                const value = f.format ? f.format(product) : product[f.key] || '—';
                return f.stacked ? (
                  <div key={f.key}>
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {f.label}
                    </dt>
                    <dd className="mt-0.5 break-all text-sm text-foreground">{value}</dd>
                  </div>
                ) : (
                  <div key={f.key} className="flex items-baseline justify-between gap-3">
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {f.label}
                    </dt>
                    <dd className="min-w-0 truncate text-sm text-foreground">{value}</dd>
                  </div>
                );
              })}
            </dl>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function ProductListPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [search, setSearch] = useState('');
  // A scanned barcode replaces whatever is in the box and searches at once.
  const scan = useBarcodeScan((code) => {
    setSearch(code);
    setPage(1);
  });
  const [isActive, setIsActive] = useState<boolean>(true);
  const [departmentId, setDepartmentId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [building, setBuilding] = useState('');
  const [sortBy, setSortBy] = useState('lastChangedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const { selectedLocationId } = useLocationStore();
  const { user, tenant } = useAuthStore();
  // Super-admin per-tenant gate: product/inventory export is OFF unless the
  // tenant is opted in (protects the catalog from bulk-export to migrate away).
  // The products service enforces the same flag server-side; this just hides
  // the action so it never looks available.
  // Export and the blank template also need the Export permission.
  const permissions = usePermissions();
  const canExportProducts = tenant?.productExportEnabled === true && permissions.has('products.export');
  const canSeeCost = useCanSeeCost();
  // What each control needs matches the products service's route gates.
  const canManageProductStatus = permissions.has('products.delete');
  const canAddProducts = permissions.has('products.add');
  const canEditPrices = permissions.has('products.prices');
  const canImportProducts = permissions.has('products.import');

  const { data: deptData } = useDepartments();
  const departments = deptData?.data ?? [];

  // Categories are scoped to the chosen department: the API returns only
  // categories that have an active product in it, so the Category filter never
  // offers a choice that would match nothing. With no department, all of them.
  const { data: categoryData, isLoading: categoriesLoading } = useCategories(departmentId ? { departmentId } : {});
  const categories = categoryData?.data ?? [];

  const { data: payeesData } = usePayees({ isActive: 'true', perPage: 200 });
  const vendors = payeesData?.data ?? [];

  // Building is free text on each product, so the Building Type choices are
  // the distinct values in use at the selected location for the current
  // Active/Inactive view — the same scope the list below is filtered by.
  const { data: buildingsData, isLoading: buildingsLoading } = useProductBuildings({
    locationId: selectedLocationId || undefined,
    isActive,
  });
  const buildings: string[] = buildingsData?.data ?? [];

  const [variantsOf, setVariantsOf] = useState<any>(null);
  // When the server refuses a delete with a structured reason (e.g. the
  // product is referenced by purchase orders), surface it in the same
  // ConfirmDialog the dept/category lists use rather than a toast — the
  // message is multi-line and actionable, so the user needs time to read it.
  const [deleteError, setDeleteError] = useState<{ title: string; message: string } | null>(null);
  // Product pending deactivation confirmation — drives the ConfirmDialog.
  const [deactivateTarget, setDeactivateTarget] = useState<{ id: string; name: string } | null>(null);
  // Product pending restore (reactivate) confirmation — the activate half of the
  // same pair, so it uses the shared ConfirmDialog too rather than a native confirm.
  const [restoreTarget, setRestoreTarget] = useState<{ id: string; name: string } | null>(null);
  // Inactive product pending permanent deletion — drives the shared ConfirmDialog
  // rather than a native confirm() so it matches the rest of the app's popups.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deactivateProduct = useDeactivateProduct();
  const restoreProduct = useRestoreProduct();
  const deleteProduct = useDeleteProduct();
  const updateProductPrice = useUpdateProductPrice();
  const recordPriceChange = useRecordPriceChange();
  const toggleProductPo = useToggleProductPo();
  // Row whose Add to PO / Remove from PO call is in flight (spinner + disabled).
  const [poPendingId, setPoPendingId] = useState<string | null>(null);
  // Putting a product on the auto PO is purchase-order work.
  const canManagePo = permissions.hasAny('purchase_orders.create', 'purchase_orders.edit');
  // Product the user tried to add to a PO without any vendor — drives the
  // shared ConfirmDialog (same guard and wording as Supernova Mobile).
  const [noVendorsFor, setNoVendorsFor] = useState<{ id: string; name: string } | null>(null);

  // Same flag and endpoint as Supernova Mobile's Find Product toggle, so a
  // change here shows there (and vice versa) on the next refresh.
  const handleTogglePo = async (product: any) => {
    const decision = decidePoToggle(product, selectedLocationId);
    if (decision.kind === 'need-location') {
      toast.error(PO_LOCATION_REQUIRED);
      return;
    }
    if (decision.kind === 'no-vendors') {
      setNoVendorsFor({ id: product.id, name: product.name });
      return;
    }
    setPoPendingId(product.id);
    try {
      await toggleProductPo.mutateAsync({
        productId: product.id,
        locationId: selectedLocationId,
        forceIncludeAutoPo: decision.next,
      });
      toast.success(poToggleSuccessMessage(product.name, decision.next));
    } catch (err: any) {
      toast.error(err?.error?.message || 'Could not update PO status.');
    } finally {
      setPoPendingId(null);
    }
  };

  // Inline price save from the product list. Same two-step flow as the full
  // product edit page (product-edit.tsx): persist the new per-location selling
  // price, then record the 'selling' change so price history / reports stay in
  // sync. Kept minimal (price only) so nothing else on the product is touched.
  const handleSavePrice = async (product: any, newPrice: number) => {
    if (!selectedLocationId) {
      throw { error: { message: 'Select a location from the dropdown before editing prices.' } };
    }
    const oldSelling = product.sellingPrice != null ? parseFloat(product.sellingPrice) : null;
    await updateProductPrice.mutateAsync({
      productId: product.id,
      locationId: selectedLocationId,
      sellingPrice: newPrice,
    });
    if (oldSelling !== newPrice) {
      recordPriceChange.mutate({
        productId: product.id,
        changes: [{ priceType: 'selling', oldValue: oldSelling, newValue: newPrice }],
      });
    }
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<{
    inserted: number;
    updated: number;
    /** Rows dropped by the client-side parser before reaching the API
     *  (e.g. case rows missing "Units in Case" / "Selling Price per Case"). */
    skipped: number;
    /** Rows the API rejected for naming a department, category or vendor that
     *  doesn't exist. Import matches these by name and never creates them. */
    failed: number;
    failures: ImportFailure[];
    /** The same rejections grouped by what was missing — the actionable view:
     *  one unknown vendor usually explains hundreds of failed rows. */
    failureSummary: ImportFailureSummary[];
    errors: string[];
  } | null>(null);
  const [importProgress, setImportProgress] = useState<{
    done: number;
    total: number;
    startedAt: number;
  } | null>(null);
  const milestoneRef = useRef(0);

  const createImportJob = useCreateImportJob();
  const finishImportJob = useFinishImportJob();
  const queryClient = useQueryClient();

  const [exportProgress, setExportProgress] = useState<{
    done: number;
    total: number;
    startedAt: number;
  } | null>(null);

  // Warn the user's browser while either an import or export is running.
  useEffect(() => {
    if (!importProgress && !exportProgress) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [importProgress, exportProgress]);

  const handleExportProducts = async () => {
    // Server-side gate is authoritative (products service returns 403 when the
    // tenant isn't opted in); bail early with a clear message so a stale menu
    // can't fire a request that would just 403.
    if (!canExportProducts) {
      toast.error('Product export is not enabled for this business.');
      return;
    }
    // Products are priced/stocked per location, so an export has to be scoped
    // to a chosen location — otherwise pricing/inventory columns are ambiguous
    // (the server falls back to an arbitrary location's row).
    if (!selectedLocationId) {
      toast.error('Select a location from the dropdown before exporting.');
      return;
    }
    const startedAt = Date.now();
    // The server builds the XLSX in a single request. We don't get progress
    // numbers back (the response is opaque until it lands), so total stays at
    // 0 and the banner renders an indeterminate "preparing" state.
    setExportProgress({ done: 0, total: 0, startedAt });
    try {
      const params = new URLSearchParams();
      if (selectedLocationId) params.set('locationId', selectedLocationId);
      const url = `/products-api/v1/products/export${params.toString() ? `?${params}` : ''}`;

      // The export can outlive the access-token TTL on big tenants. Mirror
      // the api-client's refresh-on-401 retry once — same shape, just inline
      // because this raw fetch needs the blob response instead of JSON.
      const doFetch = () =>
        fetch(url, {
          credentials: 'include',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        });
      let res = await doFetch();
      if (res.status === 401) {
        const refresh = await fetch('/auth-api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (refresh.ok) res = await doFetch();
      }

      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        toast.error(`Export failed (${res.status}). ${detail ? detail.slice(0, 120) : ''}`);
        return;
      }

      const total = Number(res.headers.get('X-Total-Products')) || 0;
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'products_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);

      toast.success(total > 0 ? `Exported ${total.toLocaleString()} products` : 'Export complete');
    } catch (err: any) {
      const msg = err?.message || 'request failed';
      toast.error(`Export failed: ${msg}`);
    } finally {
      setExportProgress(null);
    }
  };

  const handleExportTemplate = () => {
    // Same gate as the products export. There's no server call to enforce it
    // (the template is generated client-side and carries no tenant data), so
    // this check is what keeps a stale menu from handing it out.
    if (!canExportProducts) {
      toast.error('Product export is not enabled for this business.');
      return;
    }
    exportBlankTemplate();
    toast.success('Template downloaded');
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    // Pricing and inventory are stored per location, so an import must target a
    // selected location — without one the server can't write product_prices
    // rows and imported Selling Price / Cost values are silently dropped.
    if (!selectedLocationId) {
      toast.error('Select a location from the dropdown before importing.');
      return;
    }

    // Rows per /rows request. 10 rows ≈ 5KB — stays under the AWS WAF
    // SizeRestrictions_BODY 8KB default and under the server's 100-row cap.
    const CHUNK_SIZE = 10;

    try {
      const parsed = await parseProductsFromFile(file);
      if (parsed.errors.length && parsed.products.length === 0) {
        toast.error(parsed.errors.join('; '));
        return;
      }
      if (parsed.products.length === 0) {
        toast.error('No valid products found in file');
        return;
      }

      // Resolve department names → IDs using loaded departments
      const deptLookup = new Map(departments.map((d: any) => [normalizeCatalogName(d.name).toLowerCase(), d.id]));
      const productsPayload = parsed.products.map((p) => {
        const { departmentName, ...rest } = p;
        const departmentId = departmentName ? deptLookup.get(departmentName.toLowerCase()) : undefined;
        return { ...rest, departmentId, departmentName };
      });

      const total = productsPayload.length;
      const startedAt = Date.now();
      milestoneRef.current = 0;
      setImportProgress({ done: 0, total, startedAt });

      // Open a history row up front so the import shows in Import History
      // even while it's still running.
      let jobId: string | null = null;
      try {
        const created = await createImportJob.mutateAsync({
          totalRows: total,
          locationId: selectedLocationId || null,
          createdByName: user?.name || user?.email || null,
        });
        jobId = created.jobId;
      } catch {
        // History row is non-critical — don't block the import if it fails.
      }

      // Seed with the parser's own drops. parseProductsFromFile rejects rows
      // before they ever reach the API — most commonly "case" rows missing
      // "Units in Case" / "Selling Price per Case ($)", or rows with no base
      // sellingPrice. Without carrying these through, the user sees a lower
      // imported count with no explanation of where the rows went.
      const agg = {
        inserted: 0,
        updated: 0,
        skipped: parsed.skipped,
        failed: 0,
        failures: [] as ImportFailure[],
        failureSummary: [] as ImportFailureSummary[],
        errors: [...parsed.errors],
      };
      // Each chunk summarises only its own rows, so the per-chunk summaries are
      // merged here on (kind, name, inactive) — otherwise one unknown vendor
      // would be listed once per chunk with a count of 10.
      const summaryIndex = new Map<string, ImportFailureSummary>();

      for (let i = 0; i < total; i += CHUNK_SIZE) {
        const chunk = productsPayload.slice(i, i + CHUNK_SIZE);
        try {
          const result = await productsApi.post<{
            inserted: number;
            updated: number;
            failed?: number;
            failures?: ImportFailure[];
            failureSummary?: ImportFailureSummary[];
            errors: string[];
          }>('/products/bulk-import', {
            products: chunk,
            locationId: selectedLocationId || undefined,
          });
          agg.inserted += result.inserted;
          agg.updated += result.updated;
          agg.failed += result.failed ?? 0;
          if (result.failures?.length) agg.failures.push(...result.failures);
          for (const f of result.failureSummary ?? []) {
            const key = `${f.kind}|${f.name.toLowerCase()}|${f.inactive ? 1 : 0}`;
            const existing = summaryIndex.get(key);
            if (existing) existing.count += f.count;
            else summaryIndex.set(key, { ...f });
          }
          agg.errors.push(...result.errors);
        } catch (err: any) {
          const rangeStart = i + 2;
          const rangeEnd = Math.min(i + CHUNK_SIZE, total) + 1;
          const msg = err?.error?.message || err?.message || 'request failed';
          agg.errors.push(`Rows ${rangeStart}–${rangeEnd}: ${msg}`);
        }

        const done = Math.min(i + CHUNK_SIZE, total);
        setImportProgress({ done, total, startedAt });

        const milestone = Math.floor(done / 1000) * 1000;
        if (milestone > milestoneRef.current && milestone > 0) {
          milestoneRef.current = milestone;
          toast.success(`${milestone.toLocaleString()} imported...`);
        }
      }

      // Departments, categories and vendors are never created by an import any
      // more — only products change, so nothing else needs invalidating.
      agg.failureSummary = [...summaryIndex.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['import-jobs'] });

      // Close out the history row.
      if (jobId) {
        try {
          await finishImportJob.mutateAsync({
            jobId,
            status: 'succeeded',
            processedRows: total,
            result: {
              inserted: agg.inserted,
              updated: agg.updated,
              failed: agg.failed,
            },
            errors: agg.errors,
          });
        } catch {
          // Non-critical — the actual import succeeded; the history row
          // will eventually show up via the watchdog.
        }
      }

      setImportResult(agg);
    } catch (err: any) {
      toast.error(err?.error?.message || 'Import failed');
    } finally {
      setImportProgress(null);
    }
  };

  const { data: variantsData } = useProducts(
    variantsOf ? { parentProductId: variantsOf.id, perPage: 50, locationId: selectedLocationId || undefined } : {},
  );

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const { data, isLoading } = useProducts({
    page,
    perPage,
    search: search || undefined,
    isActive,
    departmentId: departmentId || undefined,
    categoryId: categoryId || undefined,
    payeeId: vendorId || undefined,
    building: building || undefined,
    locationId: selectedLocationId || undefined,
    sortBy,
    sortDir,
    // Vendor rows back the Add to PO guard (a product with no vendor can't go
    // on a purchase order) — one batched lookup per page on the service side.
    include: 'payees',
  });

  const products = data?.data ?? [];
  const pagination = data?.pagination;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleDuplicate = (product: any) => {
    const cloneData = {
      name: `${product.name} (Copy)`,
      displayName: product.displayName || '',
      departmentId: product.departmentId || '',
      sellingPrice: product.sellingPrice || '',
      cost: product.cost || '',
      soldByWeight: product.soldByWeight ?? false,
      ageVerification: product.ageVerification ?? false,
      ebtEligible: product.ebtEligible ?? true,
      excludeKitchenPrint: product.excludeKitchenPrint ?? false,
      taxable: product.taxable ?? true,
      promptQuantity: product.promptQuantity ?? false,
      isVariablePrice: product.isVariablePrice ?? false,
      defaultSellingUnit: product.defaultSellingUnit || 'each',
      defaultPurchasingUnit: product.defaultPurchasingUnit || 'each',
    };
    // Store clone data in sessionStorage and navigate to create page
    sessionStorage.setItem('product-clone', JSON.stringify(cloneData));
    window.location.hash = '#/pos-management/products/create?clone=true';
  };

  const [printProduct, setPrintProduct] = useState<any>(null);
  const [printMode, setPrintMode] = useState<'thermal' | 'a4'>('thermal');
  const [labelSize, setLabelSize] = useState('40x30');
  const [copies, setCopies] = useState(1);

  const handlePrint = (product: any) => {
    setPrintProduct(product);
  };

  const printLabel = () => {
    if (!printProduct) return;
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    if (!printWindow) {
      toast.error('Popup blocked — allow popups for this site, then print again');
      return;
    }

    const size = LABEL_SIZES.find((s) => s.id === labelSize) || LABEL_SIZES[1];
    const count = Math.max(1, Math.min(100, Number(copies) || 1));
    // Always print PORTRAIT (narrower side = page width, taller side = page
    // height). Declaring a landscape page made the print default to landscape,
    // which the thermal printer wouldn't render.
    const pageW = Math.min(size.w, size.h);
    const pageH = Math.max(size.w, size.h);
    // Size the barcode by height (~30% of the label height) so short and long
    // codes render at the same height. Sizing by width made short codes scale
    // up huge vertically and overflow the sticker.
    const barcodeHeightMm = Math.max(8, Math.min(18, Math.round(pageH * 0.3)));
    const barcode = printProduct.primaryBarcode || printProduct.sku || '';
    const name = escapeHtml(printProduct.name || '');
    const price = `$${parseFloat(printProduct.sellingPrice || 0).toFixed(2)}`;
    // Keep the on-screen preview's detection: real EAN-13/8 retail barcodes encode
    // as such; anything else (e.g. "123") falls through to CODE128.
    const fmt = barcode.length === 12 || barcode.length === 13 ? 'EAN13' : barcode.length === 8 ? 'EAN8' : 'CODE128';

    const oneLabel = `
      <div class="label">
        <div class="content">
          <div class="name">${name}</div>
          ${barcode ? `<svg class="barcode"></svg><div class="barcode-value">${escapeHtml(barcode)}</div>` : ''}
          <div class="price">${price}</div>
        </div>
      </div>`;
    const labels = Array.from({ length: count }, () => oneLabel).join('');

    const pageCss =
      printMode === 'thermal'
        ? // One label per physical page, sized exactly to the thermal stock.
          // Breaks go BETWEEN labels only (never after the last) so no blank
          // label is fed out at the end.
          `@page { size: ${pageW}mm ${pageH}mm; margin: 0; }
           html, body { margin: 0; padding: 0; }
           .label { width: ${pageW}mm; height: ${pageH}mm; }
           .label + .label { page-break-before: always; }`
        : // A4 sheet: lay the copies out in a wrapping grid (Avery-style stock).
          `@page { size: A4 portrait; margin: 8mm; }
           html, body { margin: 0; padding: 0; }
           .sheet { display: flex; flex-wrap: wrap; gap: 3mm; }
           .label { width: ${pageW}mm; height: ${pageH}mm; border: 0.2mm solid #ddd; }`;

    printWindow.document.write(`
      <html><head><title>Labels — ${name}</title>
      <style>
        ${pageCss}
        .label {
          box-sizing: border-box;
          display: flex; align-items: center; justify-content: center;
          padding: 2mm; overflow: hidden;
          font-family: Arial, sans-serif; text-align: center;
        }
        /* Keep name + barcode + price as one centred block that never
           splits across the label boundary. */
        .content {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          width: 100%; max-height: 100%;
        }
        .name { font-weight: 700; font-size: 2.6mm; line-height: 1.15; margin-bottom: 0.8mm; word-break: break-word; }
        /* viewBox + an explicit width computed from the bars' aspect ratio (set
           after render, below) makes the SVG box hug the bars exactly — no
           letterbox whitespace under the barcode, so the value sits right below
           it. The value is a separate line, not JsBarcode's embedded text. */
        .barcode { display: block; height: ${barcodeHeightMm}mm; max-width: 100%; margin: 0 auto; }
        .barcode-value {
          font-family: 'Courier New', monospace; font-size: 2.2mm;
          letter-spacing: 0.4mm; margin-top: 0.8mm; white-space: nowrap;
        }
        .price { font-weight: 800; font-size: 4mm; margin-top: 0.8mm; }
      </style>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js">${'<'}/script>
      </head><body>
        ${printMode === 'a4' ? `<div class="sheet">${labels}</div>` : labels}
        <script>
          ${
            barcode
              ? `
            var opts = { width: 2, height: 60, margin: 0, displayValue: false };
            try {
              JsBarcode(".barcode", ${JSON.stringify(barcode)}, Object.assign({ format: "${fmt}" }, opts));
            } catch(e) {
              JsBarcode(".barcode", ${JSON.stringify(barcode)}, Object.assign({ format: "CODE128" }, opts));
            }
            // Convert JsBarcode's fixed px size into a viewBox, then set the
            // SVG width from the bars' aspect ratio so the box hugs the bars
            // exactly (no whitespace between the barcode and the value below).
            var HMM = ${barcodeHeightMm};
            document.querySelectorAll(".barcode").forEach(function (svg) {
              var w = parseFloat(svg.getAttribute("width")), h = parseFloat(svg.getAttribute("height"));
              if (w && h) {
                svg.setAttribute("viewBox", "0 0 " + w + " " + h);
                svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
                svg.removeAttribute("width");
                svg.removeAttribute("height");
                svg.style.width = (w / h) * HMM + "mm";
              }
            });
          `
              : ''
          }
          // Safety net: shrink each label's content to fit within the label so
          // nothing (barcode value, price) is ever clipped by overflow and the
          // content can never spill onto a second/blank label.
          function fitLabels() {
            document.querySelectorAll(".label").forEach(function (label) {
              var content = label.querySelector(".content");
              if (!content) return;
              content.style.transform = "";
              var cs = getComputedStyle(label);
              var availH = label.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
              var availW = label.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
              var scale = Math.min(1, availH / content.offsetHeight, availW / content.offsetWidth);
              if (scale < 1) {
                content.style.transform = "scale(" + scale + ")";
                content.style.transformOrigin = "center center";
              }
            });
          }
          window.onload = function () {
            setTimeout(function () {
              fitLabels();
              window.print();
            }, 300);
          };
        ${'<'}/script>
      </body></html>
    `);
    printWindow.document.close();
    setPrintProduct(null);
  };

  // Deactivation runs through the shared ConfirmDialog (below) instead of a
  // native confirm() so it matches the rest of the app's popups. The button
  // opens the dialog by setting the target; confirmDeactivate does the work.
  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const { id, name } = deactivateTarget;
    try {
      await deactivateProduct.mutateAsync(id);
      toast.success(`"${name}" deactivated`);
      setDeactivateTarget(null);
    } catch {
      toast.error('Failed to deactivate product');
    }
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    const { id, name } = restoreTarget;
    try {
      await restoreProduct.mutateAsync(id);
      toast.success(`"${name}" restored`);
      setRestoreTarget(null);
    } catch {
      toast.error('Failed to restore product');
    }
  };

  // Permanent delete goes through the shared ConfirmDialog (below); the button
  // opens it by setting deleteTarget and confirmDelete does the work.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    try {
      await deleteProduct.mutateAsync(id);
      toast.success(`"${name}" permanently deleted`);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteTarget(null);
      const apiErr = err as { error?: { code?: string; message?: string } };
      const code = apiErr?.error?.code;
      const message = apiErr?.error?.message;
      if (code === 'PRODUCT_IN_PURCHASE_ORDER' && message) {
        setDeleteError({ title: 'Cannot delete product', message });
      } else {
        toast.error('Failed to delete product');
      }
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'image',
      header: () => <span className="sr-only">Image</span>,
      cell: ({ row }) => (
        // Own uploaded image wins; otherwise fall back to the generic/common
        // image the products service resolves per row (matches the detail page).
        <ProductThumb src={row.original.imageUrl || row.original.commonImageUrl} alt={row.original.name} />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'name',
      header: () => (
        <SortHeader label="Name" field="name" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
      ),
      // SKU / Aisle / Bin no longer have their own columns — they surface in the
      // popover on the name instead.
      cell: ({ row }) => <ProductNameWithDetails product={row.original} />,
    },
    ...(canSeeCost
      ? [
          {
            accessorKey: 'cost',
            header: () => (
              <div className="text-right">
                <SortHeader label="Cost" field="cost" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
              </div>
            ),
            cell: ({ row }: any) => <div className="text-right">{formatCurrency(row.original.cost)}</div>,
          },
        ]
      : []),
    {
      accessorKey: 'sellingPrice',
      header: () => (
        <div className="text-right">
          <SortHeader
            label="Selling Price"
            field="sellingPrice"
            currentSort={sortBy}
            currentDir={sortDir}
            onSort={handleSort}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          <InlinePriceCell
            product={row.original}
            locationId={selectedLocationId}
            readOnly={!canEditPrices}
            onSave={handleSavePrice}
          />
        </div>
      ),
    },
    {
      accessorKey: 'inStockQty',
      header: 'In Stock',
      cell: ({ row }) => {
        const qty = Number(row.original.inStockQty);
        return (
          <span className={qty <= 0 ? 'text-red-600 font-medium' : ''}>
            {qty} {row.original.defaultSellingUnit || 'each'}
          </span>
        );
      },
    },
    {
      accessorKey: 'departmentName',
      header: 'Department',
      cell: ({ row }) => row.original.departmentName || '—',
    },
    {
      // A product can sit in several categories; the API sends them pre-joined
      // (", ", A–Z). Clamp long lists to two lines and keep the full set in the
      // tooltip so the row height stays even.
      accessorKey: 'categoryNames',
      header: 'Categories',
      cell: ({ row }) =>
        row.original.categoryNames ? (
          <span className="line-clamp-2 min-w-[160px] max-w-[220px]" title={row.original.categoryNames}>
            {row.original.categoryNames}
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'options',
      header: 'Options',
      cell: ({ row }) => {
        const flags: string[] = [];
        if (row.original.isVariablePrice) flags.push('Variable $');
        if (row.original.promptQuantity) flags.push('Prompt Qty');
        if (row.original.soldByWeight) flags.push('By Weight');
        if (!flags.length) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {flags.map((f) => (
              <span
                key={f}
                className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200"
              >
                {f}
              </span>
            ))}
          </div>
        );
      },
    },
    // Audit columns sit last, just ahead of the pinned Actions column — they're
    // reference data rather than something scanned per row. Most-recent-activity
    // first: who changed it and when, then who created it and when.
    {
      accessorKey: 'changedBy',
      header: 'Modified By',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">{row.original.changedBy || '—'}</span>
      ),
    },
    // "Modified" is when the change named in Modified By happened
    // (lastChangedAt), not updatedAt: updatedAt is the registers' sync cursor
    // and also moves on every sale's stock deduction, location price sync and
    // department reorder, which made an imported product read "Import · today".
    {
      accessorKey: 'lastChangedAt',
      header: () => (
        <SortHeader
          label="Modified"
          field="lastChangedAt"
          currentSort={sortBy}
          currentDir={sortDir}
          onSort={handleSort}
        />
      ),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.original.lastChangedAt ?? row.original.updatedAt)}
        </span>
      ),
    },
    {
      accessorKey: 'createdBy',
      header: 'Created By',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">{row.original.createdBy || '—'}</span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <SortHeader label="Created" field="createdAt" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
      ),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      size: 200,
      // Pin to the right so the row actions stay reachable without scrolling the
      // wide product table horizontally (handled by the shared DataTable).
      meta: { sticky: 'right' },
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          {/* Opens the variant list that used to hang off the Variations
              column's badge. Sits outside the active/inactive branches because
              that column was always rendered, so archived products keep the
              same access to their variants. */}
          {!row.original.parentProductId && (row.original.variantCount || 0) > 0 && (
            <button
              onClick={() => setVariantsOf(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
              title={`View ${row.original.variantCount} variation${row.original.variantCount > 1 ? 's' : ''}`}
            >
              <Layers className="h-4 w-4 text-blue-600" />
            </button>
          )}
          {isActive ? (
            <>
              {/* Add to PO / Remove from PO — filled when the product is on
                  the PO for the selected location, outlined when it isn't.
                  Purchase-order work, so only for users with that permission. */}
              {canManagePo &&
                (() => {
                  const inPo = isInPo(row.original);
                  const pending = poPendingId === row.original.id;
                  const label = poToggleLabel(inPo);
                  return (
                    <button
                      type="button"
                      onClick={() => handleTogglePo(row.original)}
                      disabled={pending}
                      aria-pressed={inPo}
                      aria-label={label}
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-md border text-xs font-semibold leading-none disabled:opacity-60',
                        inPo
                          ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'border-border text-muted-foreground hover:bg-muted hover:text-primary',
                      )}
                      title={label}
                    >
                      {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'PO'}
                    </button>
                  );
                })()}
              {canAddProducts && (
                <button
                  onClick={() => handleDuplicate(row.original)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                  title="Duplicate"
                >
                  <Copy className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              <button
                onClick={() => handlePrint(row.original)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                title="Print Label"
              >
                <Printer className="h-4 w-4 text-muted-foreground" />
              </button>
              {canManageProductStatus && (
                <button
                  onClick={() => setDeactivateTarget({ id: row.original.id, name: row.original.name })}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-orange-50"
                  title="Deactivate"
                >
                  <Ban className="h-4 w-4 text-orange-500" />
                </button>
              )}
            </>
          ) : (
            <>
              {canManageProductStatus && (
                <button
                  onClick={() => setRestoreTarget({ id: row.original.id, name: row.original.name })}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-green-50"
                  title="Restore"
                >
                  <RotateCcw className="h-4 w-4 text-green-600" />
                </button>
              )}
              {canManageProductStatus && (
                <button
                  onClick={() => setDeleteTarget({ id: row.original.id, name: row.original.name })}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                  title="Delete Permanently"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <PageHeader title="All Products">
        <div className="flex items-center gap-2">
          {(canExportProducts || canImportProducts) && (
            <ProductActionsMenu
              canExportProducts={canExportProducts}
              canImportProducts={canImportProducts}
              onExportProducts={handleExportProducts}
              exportProgress={exportProgress}
              onExportTemplate={handleExportTemplate}
              onImportClick={() => fileInputRef.current?.click()}
              importProgress={importProgress}
            />
          )}
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} className="hidden" />
          {canAddProducts && (
            <a
              href="#/pos-management/products/create"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Create
            </a>
          )}
        </div>
      </PageHeader>

      {/* Export progress banner — visible while the server builds and streams
          the XLSX back. A single opaque request, so the bar is indeterminate
          (animated) rather than a real percentage. */}
      {exportProgress && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Download className="h-4 w-4 animate-pulse text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-amber-900">Preparing your export — please don't close this tab</div>
              <div className="mt-0.5 text-xs text-amber-700">
                The server is building your products spreadsheet. This usually takes 5-30 seconds depending on catalog
                size.
              </div>
            </div>
          </div>
          <div className="h-2 w-full animate-pulse overflow-hidden rounded-full bg-amber-600" />
        </div>
      )}

      {/* Import progress banner — visible only while the inline chunk loop runs */}
      {importProgress &&
        (() => {
          const { done, total, startedAt } = importProgress;
          const percent = total > 0 ? Math.floor((done / total) * 100) : 0;
          const elapsedMs = Date.now() - startedAt;
          const etaMinRaw =
            done > 0
              ? Math.ceil(((elapsedMs / done) * (total - done)) / 1000 / 60)
              : Math.ceil(((total / 10) * 0.15) / 60);
          const etaMin = Math.max(1, etaMinRaw);
          return (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <Upload className="h-4 w-4 animate-pulse text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-amber-900">
                    Don't close this tab — importing {total.toLocaleString()} products (~{etaMin} min
                    {etaMin === 1 ? '' : 's'} remaining)
                  </div>
                  <div className="mt-0.5 text-xs text-amber-700">
                    {done.toLocaleString()} / {total.toLocaleString()} processed ({percent}%)
                  </div>
                </div>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-amber-200">
                <div
                  className="h-2 rounded-full bg-amber-600 transition-all duration-150"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })()}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full min-w-0 sm:flex-1 sm:w-auto sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, PLU, barcode or SKU..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            onKeyDown={scan.onKeyDown}
            onBlur={scan.onBlur}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <SearchableSelect
          className="w-48"
          value={departmentId}
          onChange={(next) => {
            setDepartmentId(next);
            // The category list is about to change with the department; a
            // category picked under the old one may no longer be offered.
            setCategoryId('');
            setPage(1);
          }}
          options={departments.map((dept: any) => ({ value: dept.id, label: dept.name }))}
          placeholder="All Departments"
          searchPlaceholder="Search departments..."
          emptyMessage="No departments found"
        />
        <SearchableSelect
          className="w-48"
          value={categoryId}
          onChange={(next) => {
            setCategoryId(next);
            setPage(1);
          }}
          options={categories.map((cat: any) => ({ value: cat.id, label: cat.name }))}
          placeholder={categoriesLoading ? 'Loading categories...' : 'All Categories'}
          searchPlaceholder="Search categories..."
          emptyMessage={departmentId ? 'No categories in this department' : 'No categories found'}
        />
        <SearchableSelect
          className="w-48"
          value={vendorId}
          onChange={(next) => {
            setVendorId(next);
            setPage(1);
          }}
          options={vendors.map((vendor: any) => ({ value: vendor.id, label: vendor.name }))}
          placeholder="All Vendors"
          searchPlaceholder="Search vendors..."
          emptyMessage="No vendors found"
        />
        <SearchableSelect
          className="w-48"
          value={building}
          onChange={(next) => {
            setBuilding(next);
            setPage(1);
          }}
          options={buildings.map((name) => ({ value: name, label: name }))}
          placeholder={buildingsLoading ? 'Loading building types...' : 'All Building Types'}
          searchPlaceholder="Search building types..."
          emptyMessage="No building types found"
        />
        <div className="flex rounded-lg border border-border">
          <button
            onClick={() => {
              setIsActive(true);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'} rounded-l-lg`}
          >
            Active
          </button>
          <button
            onClick={() => {
              setIsActive(false);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium ${!isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'} rounded-r-lg`}
          >
            Inactive
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        <>
          {/* The table scrolls inside itself rather than stretching the page, so
              the header stays frozen and the horizontal scrollbar sits at the
              bottom of the table instead of the bottom of a very long page.
              `reserve` is the height of the pinned pagination bar below. */}
          <DataTable
            table={table}
            columns={columns}
            fillViewport={{ reserve: PAGINATION_BAR_HEIGHT }}
            // The whole row opens the product page (same route as the name
            // link). DataTable skips clicks on buttons/links/inputs, so the
            // PO toggle, inline price editor and other actions still work
            // on their own.
            onRowClick={(row, event) => {
              const href = `#/pos-management/products/${row.original.id}/edit`;
              if (event.metaKey || event.ctrlKey) {
                window.open(href, '_blank', 'noopener');
                return;
              }
              window.location.hash = href;
            }}
          />
          {pagination && (
            <div className="sticky -bottom-4 z-30 -mx-4 border-t border-border bg-white px-4 shadow-[0_-2px_8px_rgba(15,23,42,0.06)] lg:-bottom-6 lg:-mx-6 lg:px-6">
              <DataTablePagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                perPage={perPage}
                onPageChange={setPage}
                perPageOptions={PER_PAGE_OPTIONS}
                onPerPageChange={handlePerPageChange}
              />
            </div>
          )}
        </>
      )}

      {/* Variants Modal */}
      {variantsOf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-[600px] max-h-[80vh] rounded-lg bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <h3 className="text-base font-semibold">Variations of {variantsOf.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatCurrency(variantsOf.sellingPrice)} — {variantsOf.primaryBarcode || 'No barcode'}
                </p>
              </div>
              <button onClick={() => setVariantsOf(null)} className="rounded-md p-1 hover:bg-muted">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              {!variantsData?.data?.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">No variants found</p>
              ) : (
                <div className="space-y-2">
                  {variantsData.data.map((v: any) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between rounded-lg border border-border p-3 hover:bg-muted/30"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{v.name}</div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {v.primaryBarcode && <span>UPC: {v.primaryBarcode}</span>}
                          {v.sku && <span>SKU: {v.sku}</span>}
                          {v.defaultSellingUnit && v.defaultSellingUnit !== 'each' && (
                            <span className="rounded bg-muted px-1.5 py-0.5">{v.defaultSellingUnit}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <span className="text-sm font-semibold">{formatCurrency(v.sellingPrice)}</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              window.location.hash = `#/pos-management/products/${v.id}/edit`;
                            }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-muted"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5 text-primary" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-border px-6 py-3 flex justify-between">
              <button
                type="button"
                onClick={() => {
                  window.location.hash = `#/pos-management/products/${variantsOf.id}/edit`;
                }}
                className="text-sm font-medium text-primary hover:underline"
              >
                Edit parent product
              </button>
              <button
                onClick={() => setVariantsOf(null)}
                className="rounded-lg border border-border px-4 py-1.5 text-sm font-medium hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Result Modal */}
      {importResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-base font-semibold">Import Complete</h3>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
              <div className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-3">
                <span className="text-sm font-medium text-green-800">New products</span>
                <span className="text-lg font-bold text-green-700">{importResult.inserted}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-blue-50 px-4 py-3">
                <span className="text-sm font-medium text-blue-800">Updated</span>
                <span className="text-lg font-bold text-blue-700">{importResult.updated}</span>
              </div>
              {importResult.failed > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-red-800">
                      Not imported — unknown department, category or vendor
                    </span>
                    <span className="text-lg font-bold text-red-700">{importResult.failed}</span>
                  </div>
                  <p className="mt-1 text-xs text-red-700">
                    Import matches these by name and never creates them. Create the missing entry, or correct the
                    spelling in the file, then re-import — the products that did import are unaffected.
                  </p>
                  {/* Grouped first: one unknown vendor typically explains every
                      failed row, and that is the single thing to fix. */}
                  <ul className="mt-3 space-y-1.5">
                    {importResult.failureSummary.map((f) => (
                      <li
                        key={`${f.kind}-${f.name}-${f.inactive ? 'i' : 'a'}`}
                        className="flex items-start justify-between gap-3 border-t border-red-200 pt-1.5 text-xs text-red-800 first:border-0 first:pt-0"
                      >
                        <span>
                          {describeImportFailure(f)}
                          <span className="ml-1 text-red-600">
                            — add it in{' '}
                            {f.kind === 'vendor' && f.inactive
                              ? 'Send Payments → Vendors'
                              : FAILURE_FIX_LOCATION[f.kind]}
                          </span>
                        </span>
                        <span className="shrink-0 font-semibold tabular-nums">
                          {f.count} product{f.count > 1 ? 's' : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {importResult.failures.length > 0 && (
                    <>
                      <div className="mt-3 max-h-40 space-y-1 overflow-y-auto text-xs text-red-700">
                        {importResult.failures.slice(0, 100).map((f) => (
                          <p key={`${f.row}-${f.barcode ?? ''}`}>
                            Row {f.row}: {f.product}
                            {f.barcode ? ` (${f.barcode})` : ''} — {f.reasons.map(describeImportFailure).join('; ')}
                          </p>
                        ))}
                        {importResult.failures.length > 100 && (
                          <p className="font-medium">
                            …and {importResult.failures.length - 100} more — download the report for the full list
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadImportFailures(importResult.failures)}
                        className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100"
                      >
                        Download report (CSV)
                      </button>
                    </>
                  )}
                </div>
              )}
              {importResult.skipped > 0 && (
                <div className="flex items-center justify-between rounded-lg bg-amber-50 px-4 py-3">
                  <span className="text-sm font-medium text-amber-800">Skipped (not imported)</span>
                  <span className="text-lg font-bold text-amber-700">{importResult.skipped}</span>
                </div>
              )}
              {importResult.errors.length > 0 && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <p className="text-xs font-medium text-orange-800 mb-1">
                    {importResult.errors.length} row issue{importResult.errors.length > 1 ? 's' : ''}
                  </p>
                  <div className="max-h-32 overflow-y-auto text-xs text-orange-700 space-y-1">
                    {importResult.errors.slice(0, 200).map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                    {importResult.errors.length > 200 && (
                      <p className="font-medium">…and {importResult.errors.length - 200} more</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setImportResult(null)}
              className="mt-4 w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Print Label Preview */}
      {printProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-4 sm:p-6 shadow-xl">
            <h3 className="mb-4 text-base font-semibold">Print Label Preview</h3>
            <div className="mb-4 rounded-lg border border-dashed border-border p-6 text-center">
              <div className="text-lg font-bold">{printProduct.name}</div>
              {printProduct.primaryBarcode && (
                <div className="mt-3">
                  <BarcodePreview value={printProduct.primaryBarcode} />
                </div>
              )}
              <div className="mt-2 text-xl font-bold">${parseFloat(printProduct.sellingPrice || 0).toFixed(2)}</div>
              {printProduct.sku && <div className="mt-1 text-xs text-muted-foreground">SKU: {printProduct.sku}</div>}
            </div>

            {/* Print options */}
            <div className="mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPrintMode('thermal')}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                    printMode === 'thermal'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  Thermal label
                </button>
                <button
                  type="button"
                  onClick={() => setPrintMode('a4')}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                    printMode === 'a4' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                  }`}
                >
                  A4 sheet
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                  Label size
                  <select
                    value={labelSize}
                    onChange={(e) => setLabelSize(e.target.value)}
                    className="rounded-lg border border-border px-2 py-1.5 text-sm text-foreground"
                  >
                    {LABEL_SIZES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                  Copies
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={copies}
                    onChange={(e) => setCopies(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                    className="rounded-lg border border-border px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setPrintProduct(null)}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={printLabel}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteError}
        title={deleteError?.title || ''}
        message={deleteError?.message || ''}
        variant="warning"
        confirmLabel="OK"
        hideCancel
        onConfirm={() => setDeleteError(null)}
        onCancel={() => setDeleteError(null)}
      />

      <ConfirmDialog
        open={!!noVendorsFor}
        title={NO_VENDORS_TITLE}
        message={NO_VENDORS_MESSAGE}
        variant="warning"
        confirmLabel="Assign vendor"
        onConfirm={() => {
          const id = noVendorsFor?.id;
          setNoVendorsFor(null);
          if (id) window.location.hash = `#/pos-management/products/${id}/edit`;
        }}
        onCancel={() => setNoVendorsFor(null)}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate product"
        message={
          deactivateTarget
            ? `Deactivate "${deactivateTarget.name}"? It will move to the Inactive tab and won't appear at POS.`
            : ''
        }
        variant="warning"
        confirmLabel="Deactivate"
        loading={deactivateProduct.isPending}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />

      <ConfirmDialog
        open={!!restoreTarget}
        title="Restore product"
        message={restoreTarget ? `Restore "${restoreTarget.name}" back to active? It will appear at POS again.` : ''}
        variant="info"
        confirmLabel="Restore"
        loading={restoreProduct.isPending}
        onConfirm={confirmRestore}
        onCancel={() => setRestoreTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete product"
        message={deleteTarget ? `Permanently delete "${deleteTarget.name}"? This cannot be undone.` : ''}
        variant="danger"
        confirmLabel="Delete"
        loading={deleteProduct.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
